// Context
import { sideEditorContext } from '@context/side-editor-context';
import useIntersection from '@hooks/use-intersection';
import useIsomorphicLayoutEffect from '@hooks/use-isomorphic-layout-effect';
import useNoRenderRef from '@hooks/use-no-render-ref';
// hooks
import useNode from '@hooks/use-node';
import usePreviousValue from '@hooks/use-previous-value';
// utils
import { classNames } from '@modules/classname-helpers';
import { logger } from '@modules/logger';
import { filter } from '@modules/react-children-utilities';
import map from '@modules/react-children-utilities/map';
import { useRect } from '@reach/rect';
// Components
import {
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  type TabsKeyboardActivation,
  TabsOrientation,
  useTabsContext,
} from '@reach/tabs';
// biome-ignore lint/suspicious/noShadowRestrictedNames: historic
// es-toolkit/compat: lodash toString(null|undefined) === ''
import { toString } from 'es-toolkit/compat';
import { motion } from 'motion/react';
import {
  type Dispatch,
  type FC,
  type PropsWithChildren,
  type ReactNode,
  type SetStateAction,
  createContext,
  isValidElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import styles from './SideEditor.module.scss';
import { triggerWhenPanelIsInvisible, triggerWhenPanelIsVisible } from './utils';

const SideEditor: FC<
  PropsWithChildren<{
    children: ReactNode;
    defaultIndex?: number;
    keyboardActivation?: TabsKeyboardActivation;
    className?: string;
    index?: number;
    onChange?: (newTab: number) => void;
  }>
> = ({ children, defaultIndex, keyboardActivation, index, onChange, className, ...rest }) => {
  const { orientation, activeTab, changeTab } = useContext(sideEditorContext);
  const isControlled = useMemo(() => {
    if (index !== undefined && onChange !== undefined) return true;
    return false;
  }, [index, onChange]);

  return (
    <Tabs
      {...rest}
      className={classNames(styles['side-editor'], className)}
      defaultIndex={defaultIndex}
      index={isControlled ? index : activeTab}
      keyboardActivation={keyboardActivation}
      onChange={isControlled ? onChange : changeTab}
      orientation={orientation}
      data-is-horizontal={(orientation === TabsOrientation.Horizontal).toString()}
    >
      {children}
    </Tabs>
  );
};

const selectedIndicatorContext = createContext<{
  setActiveTabRect: Dispatch<SetStateAction<DOMRect | undefined>>;
  showSeparator: boolean;
}>({
  setActiveTabRect: () => logger.warn('setActiveTabRect not provided for SideEditor'),
  showSeparator: false,
});

const SideEditorMenu: FC<PropsWithChildren<{ className?: string }>> = ({
  children,
  className,
  ...rest
}) => {
  const { orientation } = useContext(sideEditorContext);
  const [activeTabRect, setActiveTabRect] = useState<DOMRect>();
  const menuWrapperRef = useRef<HTMLDivElement | null>(null);
  const menuWrapperRect = useRect(menuWrapperRef, {
    observe: true,
  });
  const [showSeparator, setShowSeparator] = useState(false);

  const indicatorLeft = useMemo(() => {
    if (!menuWrapperRect?.left || !activeTabRect?.left) return 0;
    else return activeTabRect.left - menuWrapperRect.left;
  }, [activeTabRect?.left, menuWrapperRect?.left]);

  const indicatorTop = useMemo(() => {
    // The minus 1 accounts for hiding the top border in the event the first menu item is selected
    if (menuWrapperRect?.top === undefined || activeTabRect?.top === undefined) return -1;
    else return activeTabRect.top - menuWrapperRect.top - 1;
  }, [activeTabRect?.top, menuWrapperRect?.top]);

  const bottomHalfHeight = useMemo(() => {
    if (!activeTabRect) {
      return 0;
    } else {
      return document.body.clientHeight - activeTabRect.bottom;
    }
  }, [activeTabRect]);

  const helpItems = filter(
    children,
    (child) => isValidElement(child) && child?.type === SideEditorHelpItemsContainer,
  );
  const tabs = filter(
    children,
    (child) => isValidElement(child) && child?.type !== SideEditorHelpItemsContainer,
  );

  const onIntersect = useCallback((entries: IntersectionObserverEntry[]) => {
    const navContents = entries[0];
    setShowSeparator(!navContents.isIntersecting);
  }, []);

  const [rootRef] = useIntersection(onIntersect, { threshold: 0.18 });

  useEffect(() => {
    // We want to observe when the chips are inside their container which has a bounded height
    // Threshold set to 0.98 instead of 1 to make it less sensitive while animating. I think the
    // items take up more space than in the navbar for a fraction of a second so having it at 1
    // causes the border to flicker
    function initObserver() {
      const rootEl = menuWrapperRef.current;
      if (!rootEl) return;
      const itemsToObserve = rootEl.querySelectorAll(`.${styles['side-editor-menu-item']}`);
      if (!itemsToObserve) return;

      const item = itemsToObserve[itemsToObserve.length - 1];
      const observer = new IntersectionObserver(onIntersect, { threshold: 0.98, root: rootEl });

      observer.observe(item);

      return () => observer.unobserve(item);
    }

    if (window.IntersectionObserver) {
      return initObserver();
    }
  }, [onIntersect]);

  return (
    <selectedIndicatorContext.Provider value={{ setActiveTabRect, showSeparator }}>
      <div
        ref={rootRef}
        className={classNames(styles['side-editor-menu'], className)}
        data-is-horizontal={(orientation === TabsOrientation.Horizontal).toString()}
      >
        <TabList {...rest} ref={menuWrapperRef} className={styles['side-editor-menu-wrapper']}>
          {tabs}
        </TabList>
        <div
          className={styles['side-editor-menu-selected-indicator']}
          style={{
            transform: `translate3d(${indicatorLeft}px, ${indicatorTop}px, 0px)`,
            width: `${activeTabRect?.width || 0}px`,
            height: `${activeTabRect ? activeTabRect.height + 1 : 0}px`,
            '--top-half-height': `${indicatorTop}px`,
            '--bottom-half-height': `${bottomHalfHeight}px`,
          }}
        />
        {helpItems}
      </div>
    </selectedIndicatorContext.Provider>
  );
};

const SideEditorHelpItemsContainer: FC<PropsWithChildren<unknown>> = ({ children }) => {
  const { showSeparator } = useContext(selectedIndicatorContext);

  return (
    <div data-show-divider={showSeparator} className={styles['side-editor-help-items']}>
      {children}
    </div>
  );
};

interface SideEditorMenuItemProps {
  icon: ReactNode;
  title: string;
  index: number;
}

const SideEditorMenuItem: FC<PropsWithChildren<SideEditorMenuItemProps>> = ({
  icon,
  title,
  index,
}) => {
  const { selectedIndex } = useTabsContext();
  const isSelected = index === selectedIndex;
  const menuItemRef = useRef<HTMLButtonElement | null>(null);
  const { setActiveTabRect } = useContext(selectedIndicatorContext);
  const menuItemRect = useRect(menuItemRef, {
    observe: true,
  });

  // register the menu item rect to SideEditorMenu component so that it can animate the indicator
  useIsomorphicLayoutEffect(() => {
    if (isSelected && menuItemRect) {
      setActiveTabRect(menuItemRect);
    }
  }, [isSelected, selectedIndex, setActiveTabRect, menuItemRect]);

  return (
    <Tab
      className={styles['side-editor-menu-item']}
      ref={menuItemRef}
      data-is-selected={isSelected}
    >
      <div className={styles['side-editor-menu-item-icon-container']}>{icon}</div>
      <div className={styles['side-editor-menu-item-title']}>{title}</div>
    </Tab>
  );
};

export const sideEditorPanelsContainerContext = createContext<{
  sideEditorPanelsNode: HTMLDivElement | undefined;
  registerNode: (key: string, nodeEntry: NodeRegistrationEntryContent) => void;
  scrollIntoView: (
    nodeRegistrationKey: string,
    scrollType?: SideEditorScrollType,
    compensateHeightToScroll?: boolean,
  ) => void;
}>({
  sideEditorPanelsNode: undefined,
  registerNode: () => console.log('Warning! registerNode is not implemented'),
  scrollIntoView: () => console.log('Warning! scrollToView is not implemented'),
});

const sideEditorPanelsContainerInternalContext = createContext<{
  registerPanel: (key: number, panelEntry: HTMLDivElement) => void;
  registerNode: (key: string, nodeEntry: NodeRegistrationEntryContent) => void;
}>({
  registerNode: () => console.log('Warning! registerNode is not implemented'),
  registerPanel: () => console.log('Warning! registerPanel is not implemented'),
});

export type NodeRegistrationEntryContent = {
  panelBelongTo: number;
  node: HTMLDivElement;
};

export type NodeRegistrationEntry = {
  [key: string]: NodeRegistrationEntryContent;
};

export type PanelRegistrationEntry = {
  [key: string]: HTMLDivElement;
};

export enum SideEditorScrollType {
  START = 'START',
  CENTER = 'CENTER',
  END = 'END',
}

const SideEditorPanels: FC<PropsWithChildren<{ className?: string }>> = ({
  className,
  ...rest
}) => {
  const { changeTab, activeTab } = useContext(sideEditorContext);
  const [sideEditorPanelsNode, sideEditorPanelsRef] = useNode<HTMLDivElement>();
  const nodeRegistrationEntries = useRef<NodeRegistrationEntry>({});
  const panelRegistrationEntries = useRef<PanelRegistrationEntry>({});

  // memoize activeTab to prevent rerendering of the scrollIntoView function
  const activeTabNoRenderRef = useNoRenderRef(activeTab);
  // memoize changeTab to prevent rerendering of the scrollIntoView function
  const changeTabNoRenderRef = useNoRenderRef(changeTab);

  // Let any descendant of SideEditorPanels to register their node, SideEditorPanels will use these nodes
  // to calculate the destination to scroll to
  const registerNode = useCallback((key: string, nodeEntry: NodeRegistrationEntryContent) => {
    nodeRegistrationEntries.current[key] = nodeEntry;
  }, []);

  // Let the panel to register their node, SideEditorPanels will use this node to observe its hidden state
  // and can trigger functions if the hidden state changes/ This function should only be called by SideEditorPanel
  const registerPanel = useCallback((key: number, panelEntry: HTMLDivElement) => {
    panelRegistrationEntries.current[key] = panelEntry;
  }, []);

  // we implement our own scrollIntoView because the native scrollIntoView method is unreliable for
  // deeply nested component like AccordionItem and hidden panels...
  const scrollIntoView = useCallback(
    (
      nodeRegistrationKey: string,
      scrollType: SideEditorScrollType = SideEditorScrollType.CENTER,
      compensateHeightToScroll = true,
    ) => {
      const divNode = nodeRegistrationEntries.current[nodeRegistrationKey].node;
      const panelIndexBelongTo = nodeRegistrationEntries.current[nodeRegistrationKey].panelBelongTo;
      const panelNode = panelRegistrationEntries.current[panelIndexBelongTo];
      if (!panelNode) return;
      function scrollAction() {
        if (!divNode || !sideEditorPanelsNode) return;

        const divRect = divNode.getBoundingClientRect();
        const containerRect = sideEditorPanelsNode.getBoundingClientRect();
        let distanceToScroll = 0;
        switch (scrollType) {
          case SideEditorScrollType.CENTER:
            {
              distanceToScroll = distanceToScroll =
                divRect.top -
                containerRect.top +
                sideEditorPanelsNode.scrollTop -
                sideEditorPanelsNode.clientHeight / 2;

              // when we want to scroll a element to the center, we need to add some height to the panel
              // to make sure the element is actually in the center
              const fullHeightNeededToGoCenter =
                distanceToScroll + sideEditorPanelsNode.clientHeight;
              if (fullHeightNeededToGoCenter > panelNode.clientHeight && compensateHeightToScroll) {
                panelNode.style.height = `${distanceToScroll + sideEditorPanelsNode.clientHeight + 100}px`;

                // when the panel is hidden, i.e when the users switch to different tab, reset the height to
                // its default height
                triggerWhenPanelIsInvisible(panelNode, () => {
                  panelNode.style.height = '';
                });
              }
            }
            break;
          case SideEditorScrollType.END:
            distanceToScroll =
              divRect.top -
              containerRect.top +
              sideEditorPanelsNode.scrollTop -
              sideEditorPanelsNode.clientHeight +
              divNode.clientHeight;
            break;
          case SideEditorScrollType.START: {
            distanceToScroll =
              divRect.top - containerRect.top + sideEditorPanelsNode.scrollTop - 20;

            // when we want to scroll a element to the top, we need to add some height to the panel
            // to make sure the element is actually at the top
            const fullHeightNeededToGoTop = distanceToScroll + sideEditorPanelsNode.clientHeight;
            if (fullHeightNeededToGoTop > panelNode.clientHeight && compensateHeightToScroll) {
              panelNode.style.height = `${distanceToScroll + sideEditorPanelsNode.clientHeight + 100}px`;

              // when the panel is hidden, i.e when the users switch to different tab, reset the height to
              // its default height
              triggerWhenPanelIsInvisible(panelNode, () => {
                panelNode.style.height = '';
              });
            }
            break;
          }
          default:
            break;
        }
        sideEditorPanelsNode.scrollTo({ behavior: 'smooth', top: distanceToScroll });
      }

      if (activeTabNoRenderRef.current !== panelIndexBelongTo)
        // if the element is in a non-active tab, we need to change tab first before we scroll
        changeTabNoRenderRef.current(panelIndexBelongTo);

      if (panelNode.hidden) {
        // if we scroll immediately when the panel is not yet visible, the scroll action wont have
        // any effect, so we need to wait for the panel to be visible first and then scroll
        triggerWhenPanelIsVisible(panelNode, () => scrollAction());
      } else scrollAction();
    },
    [activeTabNoRenderRef, changeTabNoRenderRef, sideEditorPanelsNode],
  );

  return (
    <sideEditorPanelsContainerContext.Provider
      value={{
        scrollIntoView,
        sideEditorPanelsNode,
        registerNode,
      }}
    >
      <sideEditorPanelsContainerInternalContext.Provider value={{ registerPanel, registerNode }}>
        <TabPanels
          ref={sideEditorPanelsRef}
          {...rest}
          className={classNames(styles['side-editor-panels'], className)}
        />
      </sideEditorPanelsContainerInternalContext.Provider>
    </sideEditorPanelsContainerContext.Provider>
  );
};

const actualPanelAnimationVariants = {
  visible: {
    opacity: 1,
    transition: {
      duration: 0.1,
      staggerChildren: 0.1,
      when: 'beforeChildren',
    },
  },
  hidden: {
    opacity: 0,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const panelItemAnimationVariants = {
  visible: (direction: boolean) => ({
    opacity: 1,
    x: direction ? [30, 0] : [-30, 0],
    transition: {
      duration: 0.2,
      ease: 'easeInOut',
    },
  }),
  hidden: (direction: boolean) => {
    return {
      opacity: 0,
      x: direction ? [0, 43] : [0, -30],
      transition: {
        duration: 0.2,
        ease: 'easeIn',
      },
    };
  },
};

export const sideEditorPanelContext = createContext<{
  panelIndex: number;
}>({
  panelIndex: -1,
});
const SideEditorPanel: FC<
  PropsWithChildren<{ children: ReactNode; className?: string; index: number }>
> = ({ children, className, index, ...rest }) => {
  const { registerPanel } = useContext(sideEditorPanelsContainerInternalContext);
  const { activeTab } = useContext(sideEditorContext);

  const [panelNode, panelNodeRef] = useNode<HTMLDivElement>();
  const previousSelectedIndex = usePreviousValue(
    activeTab,
    (prevIndex, currentIndex) => prevIndex === currentIndex,
  );
  // Typescript is complaining so this
  const actualPreviousSelectedIndex = previousSelectedIndex ?? -1;
  const isSelected = index === activeTab;

  useEffect(() => {
    if (!panelNode) return;
    registerPanel(index, panelNode);
  }, [index, panelNode, registerPanel]);

  return (
    <TabPanel tabIndex={-1} ref={panelNodeRef} {...rest}>
      <sideEditorPanelContext.Provider value={{ panelIndex: index }}>
        <motion.div
          animate={isSelected ? 'visible' : 'hidden'}
          variants={actualPanelAnimationVariants}
          className={classNames(styles['side-editor-panel'], className)}
        >
          {map(children, (child, index, arr) => {
            if (isValidElement(child)) {
              const currentIndex = index ?? 0;
              const lengthOfArray = arr?.length ?? 0;
              return (
                <motion.div
                  className={styles['side-editor-panel-wrapper']}
                  variants={panelItemAnimationVariants}
                  custom={actualPreviousSelectedIndex > activeTab}
                  key={index}
                  transition={{ type: 'spring', bounce: 0.15 }}
                  data-is-last={toString(currentIndex + 1 === lengthOfArray)}
                >
                  {child}
                </motion.div>
              );
            }
          })}
        </motion.div>
      </sideEditorPanelContext.Provider>
    </TabPanel>
  );
};

export {
  SideEditor,
  SideEditorHelpItemsContainer,
  SideEditorMenu,
  SideEditorMenuItem,
  SideEditorPanel,
  SideEditorPanels,
};
